import React, { useState, useEffect } from 'react';
import { visitorAPI } from '../services/api.ts';

interface VisitorCounterProps {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const VisitorCounter: React.FC<VisitorCounterProps> = ({ 
  className = '', 
  showLabel = true,
  size = 'md'
}) => {
  const [uniqueVisitors, setUniqueVisitors] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVisitorCount = async () => {
      try {
        setLoading(true);
        const response = await visitorAPI.getVisitorCount();
        setUniqueVisitors(response.data.data.uniqueVisitors);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch visitor count:', err);
        setError('Failed to load visitor count');
      } finally {
        setLoading(false);
      }
    };

    fetchVisitorCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchVisitorCount, 30000);

    return () => clearInterval(interval);
  }, []);

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <div className={`${iconSizes[size]} animate-spin rounded-full border-2 border-gray-300 border-t-blue-600`}></div>
        {showLabel && (
          <span className={`${sizeClasses[size]} text-gray-500`}>
            Loading...
          </span>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <div className={`${iconSizes[size]} text-red-500`}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        {showLabel && (
          <span className={`${sizeClasses[size]} text-red-500`}>
            Error
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <div className={`${iconSizes[size]} text-blue-600`}>
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
      </div>
      {showLabel && (
        <span className={`${sizeClasses[size]} text-gray-700 font-medium`}>
          {uniqueVisitors.toLocaleString()} visitors
        </span>
      )}
      {!showLabel && (
        <span className={`${sizeClasses[size]} text-gray-700 font-bold`}>
          {uniqueVisitors.toLocaleString()}
        </span>
      )}
    </div>
  );
};

export default VisitorCounter;
